import { JSX } from 'preact';
import { ColorPicker } from '@components/color-picker';
import { IcoCheck, IcoArrowRight } from '@components/icons';

export type BulletStyle = 'check-circle' | 'check' | 'arrow' | 'circle' | 'disc';

export const BULLET_STYLE_OPTIONS: Array<{ value: BulletStyle; label: string }> = [
  { value: 'disc', label: 'Disc' },
  { value: 'circle', label: 'Circle' },
  { value: 'check', label: 'Check' },
  { value: 'check-circle', label: 'Check Circle' },
  { value: 'arrow', label: 'Arrow' },
];

type BulletStyleConfig = {
  bulletStyle?: string;
  bulletColor?: string;
};

const DEFAULT_BULLET_STYLE: BulletStyleConfig = {
  bulletStyle: 'disc',
  bulletColor: '#000000',
};

type BulletIconProps = JSX.SVGAttributes<SVGSVGElement> & {
  style: BulletStyle;
  color?: string;
};

type BulletStyleCSSProps = {
  blockId: string;
} & Partial<BulletStyleConfig>;

type BulletStyleSettingsProps = {
  blockState: Partial<BulletStyleConfig>;
  onBlockStateChange?: (updater: (state: any) => any) => void;
};

const SVG_INFO: Record<BulletStyle, { content: string; viewBox: string }> = {
  'check-circle': {
    viewBox: '0 0 20 20',
    content:
      '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>',
  },
  check: {
    viewBox: '0 0 24 24',
    content: '<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>',
  },
  arrow: {
    viewBox: '0 0 24 24',
    content: '<path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>',
  },
  circle: {
    viewBox: '0 0 24 24',
    content: '<circle cx="12" cy="12" r="6"/>',
  },
  disc: {
    viewBox: '0 0 24 24',
    content: '<circle cx="12" cy="12" r="4"/>',
  },
};

export function getSvgInfo(style: BulletStyle): { content: string; viewBox: string } {
  return SVG_INFO[style] || SVG_INFO.disc;
}

export function getSvgForStyle(style: BulletStyle): string {
  const { viewBox, content } = getSvgInfo(style);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" fill="currentColor">${content}</svg>`;
}

export function svgToDataUrl(svg: string, color = 'currentColor'): string {
  const coloredSvg = svg.replace(/currentColor/g, color);
  const encodedSvg = encodeURIComponent(coloredSvg);
  return `url("data:image/svg+xml,${encodedSvg}")`;
}

export function BulletIcon({ style, color, ...props }: BulletIconProps) {
  const iconProps = {
    ...props,
    class: `${props.class || ''} shrink-0`,
    style: color ? { color } : undefined,
  };

  if (style === 'check') {
    return <IcoCheck {...iconProps} />;
  }

  if (style === 'arrow') {
    return <IcoArrowRight {...iconProps} />;
  }

  const { viewBox, content } = getSvgInfo(style);

  return (
    <svg
      viewBox={viewBox}
      fill="currentColor"
      {...iconProps}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

function generateBulletCss(style: BulletStyle, color: string, blockId: string): string {
  const svg = getSvgForStyle(style);
  const dataUrl = svgToDataUrl(svg, color);

  return `
  #${blockId} ul {
    list-style: none !important;
    padding-left: 0.5rem !important;
  }

  #${blockId} ul li {
    position: relative !important;
    padding-left: 2.4rem !important;
    margin-bottom: 0.5em !important;
  }

  #${blockId} ul li::before {
    content: "" !important;
    display: block !important;
    position: absolute !important;
    left: 0 !important;
    top: 0.33em !important;
    height: 1.4rem !important;
    width: 1.4rem !important;
    background-image: ${dataUrl} !important;
    background-repeat: no-repeat !important;
    background-size: contain !important;
    background-position: center !important;
  }`;
}

export function BulletStyleCSS({ blockId, bulletStyle, bulletColor }: BulletStyleCSSProps) {
  const style = bulletStyle || DEFAULT_BULLET_STYLE.bulletStyle;
  const color = bulletColor || DEFAULT_BULLET_STYLE.bulletColor;

  if (bulletStyle === 'none' && bulletColor === 'none') {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: generateBulletCss(style as BulletStyle, color as string, blockId),
      }}
    />
  );
}

export function BulletStyleSettings({ blockState, onBlockStateChange }: BulletStyleSettingsProps) {
  const bulletStyle = blockState.bulletStyle || DEFAULT_BULLET_STYLE.bulletStyle;
  const bulletColor = blockState.bulletColor || DEFAULT_BULLET_STYLE.bulletColor;

  return (
    <div class="space-y-6">
      <section>
        <div class="grid grid-cols-5 gap-2 p-1 bg-gray-50 rounded">
          {BULLET_STYLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              class={`p-2 rounded flex justify-center ${
                bulletStyle === option.value
                  ? 'bg-white shadow border border-gray-200'
                  : 'hover:bg-gray-100 border border-transparent'
              }`}
              onClick={() =>
                onBlockStateChange &&
                onBlockStateChange((s: any) => ({ ...s, bulletStyle: option.value }))
              }
              title={option.label}
            >
              <BulletIcon style={option.value as BulletStyle} class="size-5 shrink-0" />
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3 class="font-medium mb-3">Bullet Color</h3>
        <div class="flex items-center">
          <ColorPicker
            name="bulletColor"
            color={bulletColor}
            onPick={(color) =>
              onBlockStateChange && onBlockStateChange((s: any) => ({ ...s, bulletColor: color }))
            }
            class="border border-gray-200 rounded"
          />
        </div>
      </section>
    </div>
  );
}
